import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { useOnReconnect } from "@/hooks/useOnReconnect";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { AlunoFormDialog } from "@/components/forms/AlunoFormDialog";
import { Plus, Trash2, Users, Pencil, Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { ListaConfigDialog } from "@/components/ListaConfigDialog";

export default function Alunos() {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [alunos, setAlunos] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [dialog, setDialog] = useState(null); // {aluno, preview} | null
  const [confirmTxt, setConfirmTxt] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/alunos").then((r) => setAlunos(r.data));
  useEffect(() => { load(); }, []);
  useOnReconnect(() => { load(); });

  const openNew = () => { setEditing(null); setOpen(true); };
  const openEdit = (e, a) => { e.stopPropagation(); setEditing(a); setOpen(true); };

  const abrirExclusao = async (e, a) => {
    e.stopPropagation();
    setBusy(true); setConfirmTxt("");
    try {
      const { data } = await api.post("/admin/limpeza/alunos/preview", { aluno_ids: [a.id] });
      setDialog({ aluno: a, preview: data });
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const confirmar = async () => {
    if (!dialog) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/limpeza/alunos", { aluno_ids: [dialog.aluno.id], confirm: confirmTxt });
      const x = data.excluidos;
      toast.success(`Aluno removido com ${x.avaliacoes} avaliação(ões), ${x.formativas} formativa(s), ${x.inscricoes} inscrição(ões), ${x.ocorrencias} ocorrência(s) e ${x.presencas} presença(s).`);
      setDialog(null); load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const det = dialog?.preview?.detalhes?.[0];
  const tot = dialog?.preview?.totais;

  return (
    <div>
      <button data-testid="voltar-btn" onClick={() => navigate("/")} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-3">
        <span aria-hidden>←</span> Voltar
      </button>
      <PageHeader title="Alunos" subtitle="Fichas individuais dos alunos">
        {isAdmin && (
          <div className="flex gap-2">
            <ListaConfigDialog onChanged={load} />
            <Button data-testid="novo-aluno-btn" onClick={openNew}><Plus className="h-4 w-4 mr-2" />Novo aluno</Button>
          </div>
        )}
      </PageHeader>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {alunos.map((a) => (
          <Card key={a.id} data-testid={`aluno-${a.id}`} onClick={() => navigate(`/alunos/${a.id}`)}
            className="p-5 bg-card border-border cursor-pointer hover:border-primary/60 hover:bg-card/70 transition-colors">
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-center gap-3 min-w-0">
                <div className="p-2 rounded-full bg-amber-500/15"><Users className="h-5 w-5 text-amber-400" /></div>
                <div className="min-w-0">
                  <div className="font-semibold leading-tight truncate">{a.nome}</div>
                  <div className="text-xs text-muted-foreground font-mono">{a.nome_guerra ? `"${a.nome_guerra}" · ` : ""}{a.matricula || "—"}</div>
                </div>
              </div>
              {isAdmin && (
                <div className="flex items-center gap-2 shrink-0">
                  <button data-testid={`edit-aluno-${a.id}`} onClick={(e) => openEdit(e, a)} className="text-muted-foreground hover:text-primary"><Pencil className="h-4 w-4" /></button>
                  <button data-testid={`del-aluno-${a.id}`} onClick={(e) => abrirExclusao(e, a)} className="text-muted-foreground hover:text-red-400"><Trash2 className="h-4 w-4" /></button>
                </div>
              )}
            </div>
            <div className="mt-3 flex flex-wrap gap-1.5">
              <Badge className="bg-secondary text-secondary-foreground border-border">{a.turma_nome || "Sem turma"}</Badge>
              {a.grau_nome && <Badge className="bg-secondary text-secondary-foreground border-border" data-testid={`aluno-grau-${a.id}`}>{a.grau_nome}</Badge>}
              {a.instituicao_nome && <Badge className="bg-primary/15 text-primary border-primary/30">{a.instituicao_nome}</Badge>}
              {a.pais_nome && <Badge className="bg-secondary text-secondary-foreground border-border">{a.pais_nome}</Badge>}
            </div>
            <span className="text-xs text-primary mt-3 inline-block" data-testid={`ver-aluno-${a.id}`}>Ver histórico e evolução →</span>
          </Card>
        ))}
        {!alunos.length && <p className="text-muted-foreground text-sm">Nenhum aluno cadastrado.</p>}
      </div>

      <AlunoFormDialog open={open} onOpenChange={setOpen} aluno={editing} onSaved={load} />

      <AlertDialog open={!!dialog} onOpenChange={(v) => { if (!v) setDialog(null); }}>
        <AlertDialogContent className="bg-popover border-border max-w-lg" data-testid="excluir-aluno-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-red-400" />Excluir aluno e dados vinculados</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm">
                <p>Excluir <b className="text-foreground">{dialog?.aluno?.nome}</b> removerá também, de forma <b>definitiva</b>:</p>
                {tot && (
                  <div className="rounded-lg border border-border bg-secondary/20 p-3 font-mono text-xs space-y-1">
                    <div>Avaliações práticas: <b className="text-foreground">{tot.avaliacoes}</b></div>
                    <div>Avaliações formativas: <b className="text-foreground">{tot.formativas}</b></div>
                    <div>Inscrições: <b className="text-foreground">{tot.inscricoes}</b></div>
                    <div>Ocorrências: <b className="text-foreground">{tot.ocorrencias}</b></div>
                    <div>Presenças (capacitados): <b className="text-foreground">{tot.presencas}</b></div>
                  </div>
                )}
                {det?.vinculo_valido && (
                  <p className="text-amber-300 text-xs" data-testid="aviso-vinculo-valido">
                    ⚠️ Este aluno possui vínculo com uma turma ativa/válida. Confirme que deseja realmente removê-lo do sistema.
                  </p>
                )}
                <div>
                  <p className="mb-1">Para confirmar, digite <b>EXCLUIR</b>:</p>
                  <Input data-testid="confirm-aluno-input" value={confirmTxt} onChange={(e) => setConfirmTxt(e.target.value)} placeholder="EXCLUIR" autoFocus />
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="excluir-aluno-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction data-testid="excluir-aluno-confirmar" disabled={confirmTxt.trim().toUpperCase() !== "EXCLUIR" || busy}
              onClick={(e) => { e.preventDefault(); confirmar(); }} className="bg-red-600 hover:bg-red-700 text-white">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Excluir definitivamente"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
